/*
 *
 * DrawersPanel
 *
 */

import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useInjectReducer } from 'utils/injectReducer';
import { createStructuredSelector } from 'reselect';
import {
  makeSelectCommonColorDrawers,
  makeSelectCounterSelectedDrawersInShelvesSelected,
  makeSelectDrawersSelected,
  makeSelectIsSameDrawersNumberInShelvesSelected,
} from './selectors';
import reducer from './reducer';
import { LevelSelection, RootState } from './types';
import Panel from 'components/Panel';
import { selectionDrawersAction } from 'containers/DrawersPanel/actions';
import DrawersColorsPicker from 'containers/DrawersPanel/DrawersColorsPicker';
import { useInjectSaga } from 'utils/injectSaga';
import saga from './saga';
import DrawersTypePicker from 'containers/DrawersPanel/DrawersTypePicker';
import { makeSelectIsVisibleColorsPanel } from 'containers/Configurator/selectors/general';
import { setColorsPanelAction } from 'containers/Configurator/actions/general';
import BigOptionButton from 'components/BigOptionButton';
import compositionImg from 'assets/images/composition/composition.svg';
import { useTranslation } from 'react-i18next';
import DrawersOptions from 'containers/DrawersCompositionPanel/DrawersOptions';
import useMedia from 'use-media';
import PanelSelection from 'components/PanelSelection';
import _ from 'lodash';
import GenericObjectSelector, {
  SelectorSize,
} from 'components/GenericObjectSelector';
import { Color } from 'lib/modelBuilder/entities/Color';
import DrawerCompositionPanelContent from 'containers/DrawersCompositionPanel/DrawerCompositionPanelContent';
import AllSelector from '../../components/AllSelector';
import { ActivePanel } from '../Configurator/constants/general';
import PanelSwitcher from '../PanelSwitcher';

interface StateProps {
  isVisible: boolean;
  drawersSelected: LevelSelection;
  sameDrawersNumber: boolean;
  counterDrawer: number | null;
  commonColors: (Color | undefined)[] | undefined;
}

const stateSelector = createStructuredSelector<RootState, StateProps>({
  isVisible: makeSelectIsVisibleColorsPanel(ActivePanel.drawers),
  drawersSelected: makeSelectDrawersSelected(),
  sameDrawersNumber: makeSelectIsSameDrawersNumberInShelvesSelected(),
  counterDrawer: makeSelectCounterSelectedDrawersInShelvesSelected(),
  commonColors: makeSelectCommonColorDrawers(),
});

const key = 'drawersPanel';

function DrawersPanel() {
  useInjectReducer({ key: key, reducer: reducer });
  useInjectSaga({ key: key, saga: saga });
  const dispatch = useDispatch();
  const {
    isVisible,
    drawersSelected,
    counterDrawer,
    commonColors,
  } = useSelector(stateSelector);
  const [drawersComposition, setVisibleDrawersComposition] = useState(false);
  const { t } = useTranslation();
  const isMobile = useMedia({ maxWidth: 1023 });

  // useEffect(() => {
  //   if (isVisible && isMobile) {
  //     dispatch(selectDrawerShelvesAction());
  //   }
  // }, [isVisible, isMobile]);

  const hidePanel = () => {
    dispatch(setColorsPanelAction());
    setVisibleDrawersComposition(false);
  };

  function createDrawerArray() {
    const selection = [];
    if (counterDrawer !== null) {
      for (let i = 0; i < counterDrawer; i++) {
        selection.push(i);
      }
    }
    return selection;
  }

  function selection(e: any, indice: number) {
    if (Array.isArray(drawersSelected)) {
      if (isMobile) {
        dispatch(selectionDrawersAction([indice]));
        dispatch(setColorsPanelAction(ActivePanel.drawers, false));
      } else {
        if (drawersSelected.includes(indice)) {
          if (e.shiftKey && drawersSelected.length > 1) {
            dispatch(
              selectionDrawersAction(drawersSelected.filter(e => e !== indice)),
            );
          } else {
            dispatch(selectionDrawersAction([indice]));
          }
        } else {
          if (e.shiftKey) {
            dispatch(selectionDrawersAction([...drawersSelected, indice]));
          } else {
            dispatch(selectionDrawersAction([indice]));
          }
        }
      }
    }
  }

  const drawers = createDrawerArray();

  if (isMobile) {
    if (drawersComposition) {
      return (
        <div className="flex justify-center items-center w-full overflow-y-auto">
          <DrawersOptions hideOptionsFunction={setVisibleDrawersComposition} />
        </div>
      );
    }
    return (
      <>
        <div className="flex w-full">
          <div className="flex w-full flex-row justify-around items-center">
            <div className="drawers-selector flex flex-col-reverse">
              {drawersSelected === 'All'
                ? null
                : drawers.map(i => (
                    <GenericObjectSelector
                      key={i}
                      title={`${t('Drawer')} ${i + 1}`}
                      selected={false}
                      onClick={(e: any) => {
                        selection(e, i);
                      }}
                      color={commonColors && commonColors[i]}
                      present={counterDrawer !== null && i < counterDrawer}
                      canAddNew={false}
                      size={SelectorSize.small}
                    />
                  ))}
            </div>
            {counterDrawer === null ||
              (counterDrawer > 1 && (
                <div className="all-drawers-selector">
                  <AllSelector
                    onClick={() => {
                      dispatch(
                        selectionDrawersAction(_.range(0, drawers.length)),
                      );
                      dispatch(
                        setColorsPanelAction(ActivePanel.drawers, false),
                      );
                    }}
                  >
                    {t('All drawers')}
                  </AllSelector>
                </div>
              ))}
            <div className="composition-drawers-btn">
              <div className="self-center">
                <BigOptionButton
                  imgSrc={compositionImg}
                  title={t('Composition')}
                  handleClick={() => {
                    setVisibleDrawersComposition(true);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        <PanelSelection close={hidePanel} isVisible={isVisible}>
          <div className="flex h-full mt-4">
            <div className="mr-1 h-full">
              <DrawersColorsPicker />
            </div>
            <div>
              <DrawersTypePicker />
            </div>
          </div>
        </PanelSelection>
      </>
    );
  } else {
    return (
      <Panel close={hidePanel} isVisible={isVisible} autoclose={false}>
        {!drawersComposition ? (
          <div className="flex w-full">
            <div className="lg:w-1/4 flex justify-center py-2">
              <div className="flex flex-col justify-center">
                <PanelSwitcher />
                <div className="flex flex-col-reverse flex-wrap h-24">
                  {drawersSelected === 'All' ? (
                    <GenericObjectSelector
                      title={t('All drawers')}
                      selected={true}
                      onClick={undefined}
                      present={true}
                      canAddNew
                    />
                  ) : (
                    drawers.map(i => (
                      <GenericObjectSelector
                        key={i}
                        title={`${t('Drawer')} ${i + 1}`}
                        selected={drawersSelected.includes(i)}
                        onClick={(e: any) => {
                          selection(e, i);
                        }}
                        color={commonColors && commonColors[i]}
                        present={counterDrawer !== null && i < counterDrawer}
                        canAddNew={false}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>
            <div className="lg:w-3/4 flex">
              <div className="self-center">
                <BigOptionButton
                  imgSrc={compositionImg}
                  title={t('Composition')}
                  handleClick={() => {
                    setVisibleDrawersComposition(true);
                  }}
                />
              </div>
              <DrawersTypePicker />
              <div className="mr-4">
                <DrawersColorsPicker />
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-center items-center w-full">
            <DrawerCompositionPanelContent />
          </div>
        )}
      </Panel>
    );
  }
}

export default DrawersPanel;
